'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
} from '@heroicons/react/24/solid';

// Types for Bitmovin Player (imported dynamically)
type BitmovinPlayer = any;
type BitmovinPlayerConfig = any;
type BitmovinSourceConfig = any;

const CLIPBOARD_PATCH_KEY = '__bitmovinClipboardPatched__';
const CLIPBOARD_ORIGINAL_KEY = '__bitmovinClipboardOriginalWriteText__';

const fallbackCopyText = (text: string): boolean => {
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const didCopy = document.execCommand('copy');
        document.body.removeChild(textarea);
        return didCopy;
    } catch {
        return false;
    }
};

interface BitMovinVideoProps {
    videoUrl: string;
    title?: string;
    description?: string;
    autoplay?: boolean;
    muted?: boolean;
    useEdgeAuth?: boolean;
    className?: string;
    aspectRatio?: string; // e.g., '16/9', '4/3', or undefined to use className height
    onReady?: () => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onError?: (error: string) => void;
    // Analytics metadata
    contentVideoPlayer?: string;
    // Video metadata from manifestUrl API
    videoId?: string;
    videoType?: string;
    videoTitle?: string;
    videoPublishedDate?: string;
}

export default function BitMovinVideo({
    videoUrl,
    title = 'Video',
    description = '',
    autoplay = false,
    muted = false,
    useEdgeAuth = false,
    className = '',
    aspectRatio,
    onReady,
    onPlay,
    onPause,
    onEnded,
    onError,
    contentVideoPlayer = 'Bitmovin',
    videoId,
    videoType,
    videoTitle,
    videoPublishedDate,
}: BitMovinVideoProps) {
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<BitmovinPlayer | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [playerReady, setPlayerReady] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [availableQualities, setAvailableQualities] = useState<Array<{ id: string; label: string; width?: number; height?: number; bitrate?: number }>>([
        { id: 'auto', label: 'auto' }
    ]);
    const [currentQuality, setCurrentQuality] = useState<string>('auto');
    const [currentSpeed, setCurrentSpeed] = useState<string>('normal');
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(muted);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const draggedTimeRef = useRef(0);
    const isSeekingRef = useRef(false);
    const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const settingsMenuRef = useRef<HTMLDivElement>(null);
    const settingsButtonRef = useRef<HTMLButtonElement>(null);
    const playerContainerWrapperRef = useRef<HTMLDivElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const completion25LoggedRef = useRef(false);
    const completion50LoggedRef = useRef(false);
    const completion75LoggedRef = useRef(false);
    const completion100LoggedRef = useRef(false);
    const completion25TimeRef = useRef<number | null>(null);
    const completion50TimeRef = useRef<number | null>(null);
    const completion75TimeRef = useRef<number | null>(null);
    const completion100TimeRef = useRef<number | null>(null);
    const videoTerminatedRef = useRef(false);
    const previousCompletionPercentRef = useRef<number>(0);
    const hasVideoStartedRef = useRef(false);
    const [hoverTime, setHoverTime] = useState<number | null>(null);
    const [hoverPosition, setHoverPosition] = useState<number>(0);
    const [showCenterIcon, setShowCenterIcon] = useState(true);
    const [iconAnimation, setIconAnimation] = useState<'zoom-in' | 'zoom-out' | 'none'>('none');
    const centerIconTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isPiP, setIsPiP] = useState(false);
    const [supportsPiP, setSupportsPiP] = useState(false);
    const [authenticatedUrl, setAuthenticatedUrl] = useState<string>(videoUrl || '');

    // In CMS preview, clipboard-write may be blocked by Permissions-Policy.
    // Patch clipboard writes to fail gracefully so player internals do not break source handling.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const nav = navigator as Navigator & {
            [CLIPBOARD_PATCH_KEY]?: boolean;
            [CLIPBOARD_ORIGINAL_KEY]?: ((data: string) => Promise<void>) | null;
        };
        if (!nav.clipboard?.writeText || nav[CLIPBOARD_PATCH_KEY]) return;

        const originalWriteText = nav.clipboard.writeText.bind(nav.clipboard);
        nav[CLIPBOARD_ORIGINAL_KEY] = originalWriteText;
        nav[CLIPBOARD_PATCH_KEY] = true;

        nav.clipboard.writeText = (async (data: string) => {
            try {
                await originalWriteText(data);
            } catch (error) {
                const isNotAllowedError =
                    error instanceof DOMException && error.name === 'NotAllowedError';
                if (!isNotAllowedError) throw error;

                const copied = fallbackCopyText(data);
                if (!copied) {
                    console.warn('[BitMovinVideo] Clipboard write blocked in current document context.');
                }
            }
        }) as typeof nav.clipboard.writeText;
    }, []);

    // Check for Picture-in-Picture API support (Chrome, Edge, Safari - NOT Firefox)
    // Firefox uses its own native PiP button, so we hide our custom one there
    useEffect(() => {
        const checkPiPSupport = () => {
            // Check if the standard PiP API is available
            // Firefox doesn't support document.pictureInPictureEnabled properly
            const hasPiPEnabled = 'pictureInPictureEnabled' in document && document.pictureInPictureEnabled;
            setSupportsPiP(!!hasPiPEnabled);
        };
        checkPiPSupport();
    }, []);

    // Function to get Akamai EdgeAuth token and append to URL
    const getAuthenticatedUrl = useCallback(async (url: string): Promise<string> => {
        if (!useEdgeAuth) return url;

        try {
            const urlObj = new URL(url);
            const path = urlObj.pathname + urlObj.search;

            const response = await fetch(`/api/edgeauth?path=${encodeURIComponent(path)}`);
            const data = await response.json();

            if (data.success && data.token) {
                const separator = url.includes('?') ? '&' : '?';
                return `${url}${separator}${data.tokenName || 'hdnts'}=${data.token}`;
            }
            return url;
        } catch {
            return url;
        }
    }, [useEdgeAuth]);

    // Helper function to format time for display and analytics
    const formatTime = useCallback((seconds: number): string => {
        if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '00:00';
        const totalSeconds = Math.floor(seconds);
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, []);

    // Helper function to push events to Google Tag Manager dataLayer
    const pushToDataLayer = useCallback((eventData: Record<string, any>) => {
        if (typeof window !== 'undefined') {
            // Initialize dataLayer if it doesn't exist
            if (!(window as any).dataLayer) {
                (window as any).dataLayer = [];
            }

            // Push the event to dataLayer
            try {
                (window as any).dataLayer.push(eventData);
                //console.log("Pushed to dataLayer:", eventData);
            } catch (error) {
                console.error("Error pushing to dataLayer:", error);
            }
        }
    }, []);

    // Function to handle video end (pause, log termination, show play icon)
    const handleVideoEnd = useCallback(() => {
        const playerInstance = playerRef.current as any;
        if (playerInstance) {
            // Pause the video
            try {
                playerInstance.pause?.();
            } catch {
                // Ignore pause errors
            }
        }

        // Set playing state to false (shows Play icon)
        setIsPlaying(false);

        // Mark video as terminated to prevent further console logs
        videoTerminatedRef.current = true;

        // Call onEnded callback
        onEnded?.();

        // Console log for video termination event
        try {
            const playerInstance = playerRef.current as any;
            const currentTime = playerInstance?.getCurrentTime?.() ||
                playerInstance?.currentTime ||
                (playerInstance?.getVideoElement?.()?.currentTime) || 0;

            const currentTimeFormatted = formatTime(currentTime);
            const fullDuration = playerInstance?.getDuration?.() ||
                playerInstance?.duration ||
                (playerInstance?.getVideoElement?.()?.duration) || duration || 0;
            const fullDurationFormatted = formatTime(fullDuration);

            const videoTerminationData = {
                'event': 'video_termination',
                'content_id': videoId || '',
                'content_type': videoType || '',
                'content_name': videoTitle || '',
                'publish_date': videoPublishedDate || '',
                'video_timeStamp': currentTimeFormatted,
                'video_duration': fullDurationFormatted,
                'content_video_player': contentVideoPlayer || 'Bitmovin'
            };

            //console.log("Video_Termination ---", videoTerminationData);

            // Push to Google Tag Manager dataLayer
            pushToDataLayer(videoTerminationData);
        } catch (error) {
            // Ignore logging errors
        }
    }, [formatTime, onEnded, videoId, videoType, videoTitle, videoPublishedDate, contentVideoPlayer, pushToDataLayer, duration]);

    // Function to check and log video completion percentages
    const checkAndLogCompletion = useCallback((currentTime: number, videoDuration: number, isSeeking: boolean = false) => {
        // Don't log if video has been terminated
        if (videoTerminatedRef.current) return;

        if (!videoDuration || videoDuration <= 0) return;

        const completionPercent = (currentTime / videoDuration) * 100;
        const previousPercent = previousCompletionPercentRef.current;

        // Only log when crossing thresholds during normal playback (not when seeking)
        // This ensures we capture the exact moment each percentage is reached
        if (!isSeeking && previousPercent < completionPercent) {
            // Log 25% completion - only when crossing from below 25% to 25% or above
            if (previousPercent < 25 && completionPercent >= 25 && !completion25LoggedRef.current) {
                completion25LoggedRef.current = true;
                completion25TimeRef.current = currentTime;
                const timeFormatted = formatTime(currentTime);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion25Data = {
                    'event': 'video_completion_25',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_25", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion25Data);
            }

            // Log 50% completion - only when crossing from below 50% to 50% or above
            if (previousPercent < 50 && completionPercent >= 50 && !completion50LoggedRef.current) {
                completion50LoggedRef.current = true;
                completion50TimeRef.current = currentTime;
                const timeFormatted = formatTime(currentTime);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion50Data = {
                    'event': 'video_completion_50',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_50", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion50Data);
            }

            // Log 75% completion - only when crossing from below 75% to 75% or above
            if (previousPercent < 75 && completionPercent >= 75 && !completion75LoggedRef.current) {
                completion75LoggedRef.current = true;
                completion75TimeRef.current = currentTime;
                const timeFormatted = formatTime(currentTime);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion75Data = {
                    'event': 'video_completion_75',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_75", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion75Data);
            }

            // Log 100% completion - only when crossing from below 100% to 100%
            if (previousPercent < 100 && completionPercent >= 100 && !completion100LoggedRef.current) {
                completion100LoggedRef.current = true;
                completion100TimeRef.current = currentTime;
                const timeFormatted = formatTime(currentTime);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion100Data = {
                    'event': 'video_completion_100',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_100", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion100Data);

                // End the video when 100% is reached
                handleVideoEnd();
            }
        } else if (isSeeking) {
            // When seeking, check if we've passed any thresholds and log them with exact milestone times
            // This handles the case where user seeks past a threshold
            if (completionPercent >= 25 && !completion25LoggedRef.current && completion25TimeRef.current === null) {
                // Calculate the exact 25% time
                const exact25Time = videoDuration * 0.25;
                completion25LoggedRef.current = true;
                completion25TimeRef.current = exact25Time;
                const timeFormatted = formatTime(exact25Time);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion25Data = {
                    'event': 'video_completion_25',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_25", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion25Data);
            }

            if (completionPercent >= 50 && !completion50LoggedRef.current && completion50TimeRef.current === null) {
                const exact50Time = videoDuration * 0.50;
                completion50LoggedRef.current = true;
                completion50TimeRef.current = exact50Time;
                const timeFormatted = formatTime(exact50Time);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion50Data = {
                    'event': 'video_completion_50',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_50", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion50Data);
            }

            if (completionPercent >= 75 && !completion75LoggedRef.current && completion75TimeRef.current === null) {
                const exact75Time = videoDuration * 0.75;
                completion75LoggedRef.current = true;
                completion75TimeRef.current = exact75Time;
                const timeFormatted = formatTime(exact75Time);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion75Data = {
                    'event': 'video_completion_75',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_75", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion75Data);
            }

            if (completionPercent >= 100 && !completion100LoggedRef.current && completion100TimeRef.current === null) {
                const exact100Time = videoDuration;
                completion100LoggedRef.current = true;
                completion100TimeRef.current = exact100Time;
                const timeFormatted = formatTime(exact100Time);
                const fullDurationFormatted = formatTime(videoDuration);

                const videoCompletion100Data = {
                    'event': 'video_completion_100',
                    'content_id': videoId || '',
                    'content_type': videoType || '',
                    'content_name': videoTitle || '',
                    'publish_date': videoPublishedDate || '',
                    'video_timeStamp': timeFormatted,
                    'video_duration': fullDurationFormatted,
                    'content_video_player': contentVideoPlayer || 'Bitmovin'
                };

                //console.log("Video_Completion_100", { duration: timeFormatted });

                // Push to Google Tag Manager dataLayer
                pushToDataLayer(videoCompletion100Data);

                handleVideoEnd();
            }
        }

        // Update previous completion percent for next check
        previousCompletionPercentRef.current = completionPercent;
    }, [formatTime, handleVideoEnd, pushToDataLayer, videoId, videoType, videoTitle, videoPublishedDate, contentVideoPlayer]);

    // Get authenticated URL when source or EdgeAuth setting changes
    useEffect(() => {
        const fetchAuthenticatedUrl = async () => {
            if (videoUrl) {
                // If no edge auth needed, set URL directly
                if (!useEdgeAuth) {
                    setAuthenticatedUrl(videoUrl);
                    return;
                }
                // Otherwise fetch authenticated URL
                const authUrl = await getAuthenticatedUrl(videoUrl);
                setAuthenticatedUrl(authUrl);
            }
        };
        fetchAuthenticatedUrl();
    }, [videoUrl, useEdgeAuth, getAuthenticatedUrl]);

    // Initialize Bitmovin Player
    useEffect(() => {
        console.log('[BitMovinVideo] Init check - Container:', !!playerContainerRef.current, 'URL:', authenticatedUrl);

        if (!playerContainerRef.current || !authenticatedUrl) {
            console.log('[BitMovinVideo] Skipping init - missing container or URL');
            return;
        }

        let isMounted = true;
        let player: BitmovinPlayer | null = null;

        const initPlayer = async () => {
            try {
                setIsLoading(true);
                setHasError(false);

                // Dynamically import Bitmovin Player (only runs on client)
                const BitmovinPlayerModule = await import('bitmovin-player');
                const Player = BitmovinPlayerModule.Player;

                if (!isMounted || !playerContainerRef.current) {
                    console.log('[BitMovinVideo] Component unmounted during init');
                    return;
                }

                // Get license key from environment variable
                const licenseKey = process.env.NEXT_PUBLIC_BITMOVIN_LICENSE_KEY || '';
                console.log('[BitMovinVideo] License key:', licenseKey ? 'SET' : 'NOT SET');

                // Player configuration
                const playerConfig: BitmovinPlayerConfig = {
                    key: licenseKey,
                    playback: {
                        autoplay: autoplay,
                        muted: muted,
                    },
                    ui: false, // Disable default UI since we're using custom controls
                    adaptation: {
                        desktop: {
                            preload: true,
                        },
                    },
                };

                // Create player instance
                player = new Player(playerContainerRef.current, playerConfig);
                playerRef.current = player;

                // Player event listeners
                player.on('ready' as any, () => {
                    setIsLoading(false);
                    setPlayerReady(true);
                    onReady?.();
                    try {
                        const playerInstance = player as any;
                        const playing = playerInstance.isPlaying?.() || playerInstance.playing || false;
                        setIsPlaying(playing);
                    } catch {
                        // Ignore
                    }
                });

                player.on('play' as any, () => {
                    setIsPlaying(true);
                    onPlay?.();

                    // Console log for video start event
                    try {
                        const playerInstance = player as any;
                        const currentTime = playerInstance.getCurrentTime?.() ||
                            playerInstance.currentTime ||
                            (playerInstance.getVideoElement?.()?.currentTime) || 0;

                        // Only reset completion tracking if video is starting from the beginning (near 0)
                        if (currentTime < 1) {
                            completion25LoggedRef.current = false;
                            completion50LoggedRef.current = false;
                            completion75LoggedRef.current = false;
                            completion100LoggedRef.current = false;
                            completion25TimeRef.current = null;
                            completion50TimeRef.current = null;
                            completion75TimeRef.current = null;
                            completion100TimeRef.current = null;
                            previousCompletionPercentRef.current = 0;
                            // Reset termination flag when starting from beginning
                            videoTerminatedRef.current = false;
                            // Reset video started flag when starting from beginning
                            hasVideoStartedRef.current = false;
                        }

                        // Don't log if video has been terminated
                        if (videoTerminatedRef.current) return;

                        const currentTimeFormatted = formatTime(currentTime);
                        const fullDuration = playerInstance.getDuration?.() ||
                            playerInstance.duration ||
                            (playerInstance.getVideoElement?.()?.duration) || duration || 0;
                        const fullDurationFormatted = formatTime(fullDuration);

                        // Determine if this is the first start or a resume
                        const isFirstStart = !hasVideoStartedRef.current;
                        const eventType = isFirstStart ? 'video_start' : 'video_resume';

                        // Mark video as started if it's the first time
                        if (isFirstStart) {
                            hasVideoStartedRef.current = true;
                        }

                        const videoEventData = {
                            'event': eventType,
                            'content_id': videoId || '',
                            'content_type': videoType || '',
                            'content_name': videoTitle || '',
                            'publish_date': videoPublishedDate || '',
                            'video_timeStamp': currentTimeFormatted,
                            'video_duration': fullDurationFormatted,
                            'content_video_player': contentVideoPlayer || 'Bitmovin'
                        };

                        //console.log(isFirstStart ? "Video_Started ---" : "Video_Resumed ---", videoEventData);

                        // Push to Google Tag Manager dataLayer
                        pushToDataLayer(videoEventData);
                    } catch (error) {
                        console.error("Error pushing to dataLayer:", error);
                    }
                });

                player.on('playing' as any, () => {
                    setIsPlaying(true);
                });

                player.on('pause' as any, () => {
                    setIsPlaying(false);
                    onPause?.();
                });

                player.on('paused' as any, () => {
                    setIsPlaying(false);

                    // Console log for video pause event
                    try {
                        // Don't log if video has been terminated
                        if (videoTerminatedRef.current) return;

                        const playerInstance = player as any;
                        const currentTime = playerInstance.getCurrentTime?.() ||
                            playerInstance.currentTime ||
                            (playerInstance.getVideoElement?.()?.currentTime) || 0;

                        const currentTimeFormatted = formatTime(currentTime);
                        const fullDuration = playerInstance.getDuration?.() ||
                            playerInstance.duration ||
                            (playerInstance.getVideoElement?.()?.duration) || duration || 0;
                        const fullDurationFormatted = formatTime(fullDuration);

                        const videoPauseData = {
                            'event': 'video_pause',
                            'content_id': videoId || '',
                            'content_type': videoType || '',
                            'content_name': videoTitle || '',
                            'publish_date': videoPublishedDate || '',
                            'video_timeStamp': currentTimeFormatted,
                            'video_duration': fullDurationFormatted,
                            'content_video_player': contentVideoPlayer || 'Bitmovin'
                        };

                        //console.log("Video_Paused ---", videoPauseData);

                        // Push to Google Tag Manager dataLayer
                        pushToDataLayer(videoPauseData);
                    } catch (error) {
                        // Ignore logging errors
                    }
                });

                player.on('ended' as any, () => {
                    // Use the shared handleVideoEnd function
                    handleVideoEnd();
                });

                player.on('timeChanged' as any, (event: any) => {
                    if (!isDraggingRef.current && !isSeekingRef.current && event?.time !== undefined && !isNaN(event.time)) {
                        setCurrentTime(event.time);
                        // Check completion percentage
                        const videoDuration = duration || 0;
                        if (videoDuration > 0) {
                            checkAndLogCompletion(event.time, videoDuration);
                        }
                    }
                });

                player.on('timeupdate' as any, (event: any) => {
                    if (!isDraggingRef.current && !isSeekingRef.current && event?.time !== undefined && !isNaN(event.time)) {
                        setCurrentTime(event.time);
                        // Check completion percentage
                        const videoDuration = duration || 0;
                        if (videoDuration > 0) {
                            checkAndLogCompletion(event.time, videoDuration);
                        }
                    }
                });

                player.on('seeked' as any, () => {
                    if (seekTimeoutRef.current) {
                        clearTimeout(seekTimeoutRef.current);
                        seekTimeoutRef.current = null;
                    }
                    isSeekingRef.current = false;

                    if (!isDraggingRef.current) {
                        setTimeout(() => {
                            const playerInstance = player as any;
                            const actualTime = playerInstance.getCurrentTime?.() ||
                                playerInstance.currentTime ||
                                (playerInstance.getVideoElement?.()?.currentTime);
                            if (actualTime !== undefined && !isNaN(actualTime) && !isDraggingRef.current) {
                                setCurrentTime(actualTime);

                                // Check completion after seek - pass isSeeking=true to use exact milestone times
                                const videoDuration = duration || 0;
                                if (videoDuration > 0) {
                                    checkAndLogCompletion(actualTime, videoDuration, true);
                                }

                                // Reset completion tracking if seeking back to the beginning (near 0)
                                if (actualTime < 1) {
                                    completion25LoggedRef.current = false;
                                    completion50LoggedRef.current = false;
                                    completion75LoggedRef.current = false;
                                    completion100LoggedRef.current = false;
                                    completion25TimeRef.current = null;
                                    completion50TimeRef.current = null;
                                    completion75TimeRef.current = null;
                                    completion100TimeRef.current = null;
                                    previousCompletionPercentRef.current = 0;
                                    // Reset termination flag when seeking back to beginning
                                    videoTerminatedRef.current = false;
                                }
                            }
                        }, 100);
                    }
                });

                player.on('seek' as any, () => {
                    isSeekingRef.current = true;
                });

                player.on('durationChanged' as any, (event: any) => {
                    if (event?.duration !== undefined && !isNaN(event.duration) && event.duration > 0) {
                        setDuration(event.duration);
                    }
                });

                // Set up periodic time updates as fallback
                const timeUpdateInterval = setInterval(() => {
                    try {
                        const playerInstance = player as any;
                        if (playerInstance && !isDraggingRef.current && !isSeekingRef.current) {
                            const time = playerInstance.getCurrentTime?.() ||
                                playerInstance.currentTime ||
                                (playerInstance.getVideoElement?.()?.currentTime);
                            if (time !== undefined && !isNaN(time) && time >= 0) {
                                setCurrentTime(time);
                            }

                            const dur = playerInstance.getDuration?.() ||
                                playerInstance.duration ||
                                (playerInstance.getVideoElement?.()?.duration);
                            if (dur !== undefined && !isNaN(dur) && dur > 0) {
                                setDuration(dur);
                                // Check completion percentage
                                if (time !== undefined && !isNaN(time) && time >= 0) {
                                    checkAndLogCompletion(time, dur);
                                }
                            }
                        }
                    } catch {
                        // Ignore errors
                    }
                }, 250);

                timeUpdateIntervalRef.current = timeUpdateInterval;

                player.on('volumeChanged' as any, (event: any) => {
                    if (event?.volume !== undefined) {
                        setVolume(event.volume);
                        setIsMuted(event.volume === 0);
                    }
                });

                player.on('videoQualityChanged' as any, (event: any) => {
                    const quality = event?.videoQuality || event?.quality;
                    if (quality) {
                        setCurrentQuality(quality.id || quality.qualityId || 'auto');
                    }
                });

                player.on('error' as any, (error: any) => {
                    const errorMsg = error?.message || error?.toString() || 'Unknown error';
                    setHasError(true);
                    setErrorMessage(errorMsg);
                    setIsLoading(false);
                    onError?.(errorMsg);
                });

                player.on('sourceLoaded' as any, () => {
                    const fetchQualities = () => {
                        try {
                            const playerInstance = player as any;
                            const qualities = playerInstance.getAvailableVideoQualities?.() || [];

                            if (qualities && qualities.length > 0) {
                                const qualityOptions = [
                                    { id: 'auto', label: 'auto' },
                                    ...qualities.map((q: any) => {
                                        const width = q.width || q.resolution?.width || 0;
                                        const height = q.height || q.resolution?.height || 0;
                                        const bitrate = q.bitrate || q.bitrateValue || 0;
                                        const bitrateKbps = bitrate ? Math.round(bitrate / 1000) : 0;
                                        const id = q.id;
                                        const label = width && height
                                            ? `${width}x${height}${bitrateKbps ? `, ${bitrateKbps}kbps` : ''}`
                                            : q.label || 'Unknown';
                                        return { id, label, width, height, bitrate };
                                    }),
                                ];
                                setAvailableQualities(qualityOptions);
                                return true;
                            }
                            return false;
                        } catch {
                            return false;
                        }
                    };

                    setTimeout(() => {
                        try {
                            const playerInstance = player as any;
                            const dur = playerInstance.getDuration?.() ||
                                playerInstance.duration ||
                                (playerInstance.getVideoElement?.()?.duration) || 0;
                            if (dur && dur > 0 && !isNaN(dur)) {
                                setDuration(dur);
                            }

                            const vol = playerInstance.getVolume?.() ?? playerInstance.volume ?? 1;
                            setVolume(vol);
                            setIsMuted(vol === 0);

                            const time = playerInstance.getCurrentTime?.() ||
                                playerInstance.currentTime ||
                                (playerInstance.getVideoElement?.()?.currentTime) || 0;
                            if (time >= 0 && !isNaN(time)) {
                                setCurrentTime(time);
                            }

                            const playing = playerInstance.isPlaying?.() ||
                                playerInstance.playing ||
                                (playerInstance.getVideoElement?.() && !playerInstance.getVideoElement().paused) || false;
                            setIsPlaying(playing);

                            fetchQualities();
                            setTimeout(() => fetchQualities(), 1000);
                            setTimeout(() => fetchQualities(), 2500);
                            setTimeout(() => fetchQualities(), 5000);
                        } catch {
                            // Ignore
                        }
                    }, 500);
                });

                player.on('ready' as any, () => {
                    setTimeout(() => {
                        try {
                            const playerInstance = player as any;
                            const qualities = playerInstance.getAvailableVideoQualities?.() || [];
                            if (qualities.length > 0) {
                                const qualityOptions = [
                                    { id: 'auto', label: 'auto' },
                                    ...qualities.map((q: any) => ({
                                        id: q.id,
                                        label: `${q.width}x${q.height}${q.bitrate ? `, ${Math.round(q.bitrate / 1000)}kbps` : ''}`,
                                        width: q.width,
                                        height: q.height,
                                        bitrate: q.bitrate
                                    })),
                                ];
                                setAvailableQualities(qualityOptions);
                            }
                        } catch { }
                    }, 1000);
                });

                player.on('videoPlaybackQualityChanged' as any, () => {
                    try {
                        const playerInstance = player as any;
                        const qualities = playerInstance.getAvailableVideoQualities?.() || [];

                        if (qualities.length > 0) {
                            const qualityOptions = [
                                { id: 'auto', label: 'auto' },
                                ...qualities.map((q: any) => {
                                    const width = q.width || q.resolution?.width || 0;
                                    const height = q.height || q.resolution?.height || 0;
                                    const bitrate = q.bitrate || q.bitrateValue || 0;
                                    const bitrateKbps = bitrate ? Math.round(bitrate / 1000) : 0;
                                    const id = q.id || q.qualityId || `${height}_${bitrate}`;
                                    const label = width && height
                                        ? `${width}x${height}${bitrateKbps ? `, ${bitrateKbps}kbps` : ''}`
                                        : q.label || 'Unknown';
                                    return { id, label, width, height, bitrate };
                                }),
                            ];
                            setAvailableQualities(qualityOptions);
                        }
                    } catch {
                        // Ignore errors
                    }
                });

                player.on('playing' as any, () => {
                    try {
                        const playerInstance = player as any;
                        const qualities = playerInstance.getAvailableVideoQualities?.() || [];

                        if (qualities.length > 0 && availableQualities.length <= 1) {
                            const qualityOptions = [
                                { id: 'auto', label: 'auto' },
                                ...qualities.map((q: any) => {
                                    const width = q.width || q.resolution?.width || 0;
                                    const height = q.height || q.resolution?.height || 0;
                                    const bitrate = q.bitrate || q.bitrateValue || 0;
                                    const bitrateKbps = bitrate ? Math.round(bitrate / 1000) : 0;
                                    const id = q.id || q.qualityId || `${height}_${bitrate}`;
                                    const label = width && height
                                        ? `${width}x${height}${bitrateKbps ? `, ${bitrateKbps}kbps` : ''}`
                                        : q.label || 'Unknown';
                                    return { id, label, width, height, bitrate };
                                }),
                            ];
                            setAvailableQualities(qualityOptions);
                        }
                    } catch {
                        // Ignore errors
                    }
                });

                player.on('sourceError' as any, (error: any) => {
                    const errorMsg = error?.message || error?.toString() || 'Source loading error';
                    setHasError(true);
                    setErrorMessage(errorMsg);
                    setIsLoading(false);
                    onError?.(errorMsg);
                });

                // Load the source
                const sourceConfig: BitmovinSourceConfig = {
                    hls: authenticatedUrl,
                    title: title,
                    description: description,
                };

                console.log('[BitMovinVideo] Loading source:', authenticatedUrl);

                player.load(sourceConfig).then(
                    () => {
                        console.log('[BitMovinVideo] Source loaded successfully');
                    },
                    (error: any) => {
                        console.error('[BitMovinVideo] Source load error:', error);
                        if (isMounted) {
                            setHasError(true);
                            setErrorMessage(error.message || 'Failed to load source');
                            setIsLoading(false);
                            onError?.(error.message || 'Failed to load source');
                        }
                    }
                );

            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                console.error('[BitMovinVideo] Init error:', errorMsg);
                if (isMounted) {
                    setHasError(true);
                    setErrorMessage(errorMsg);
                    setIsLoading(false);
                    onError?.(errorMsg);
                }
            }
        };

        // Start initialization
        initPlayer();

        // Listen for PiP changes
        const handlePiPChange = () => {
            setIsPiP(!!document.pictureInPictureElement);
        };
        document.addEventListener('enterpictureinpicture', handlePiPChange);
        document.addEventListener('leavepictureinpicture', handlePiPChange);

        // Cleanup function
        return () => {
            isMounted = false;
            if (timeUpdateIntervalRef.current) {
                clearInterval(timeUpdateIntervalRef.current);
                timeUpdateIntervalRef.current = null;
            }
            if (seekTimeoutRef.current) {
                clearTimeout(seekTimeoutRef.current);
                seekTimeoutRef.current = null;
            }
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
            if (centerIconTimeoutRef.current) {
                clearTimeout(centerIconTimeoutRef.current);
                centerIconTimeoutRef.current = null;
            }
            document.removeEventListener('enterpictureinpicture', handlePiPChange);
            document.removeEventListener('leavepictureinpicture', handlePiPChange);
        };
    }, [authenticatedUrl, autoplay, muted, title, description, onReady, onPlay, onPause, onEnded, onError, availableQualities.length, videoId, videoType, videoTitle, videoPublishedDate, contentVideoPlayer, formatTime, duration, checkAndLogCompletion, handleVideoEnd, pushToDataLayer]);

    // Simple, direct seek function
    const seekToTime = useCallback((time: number) => {
        if (!playerRef.current) return;

        const seekTime = Math.max(0, Math.min(time, duration));

        try {
            const playerInstance = playerRef.current as any;

            if (typeof playerInstance.seek === 'function') {
                playerInstance.seek(seekTime);
                setCurrentTime(seekTime);
                return;
            }

            const videoElement = playerInstance.getVideoElement?.();
            if (videoElement) {
                videoElement.currentTime = seekTime;
                setCurrentTime(seekTime);
                return;
            }
        } catch {
            // Ignore seek errors
        }
    }, [duration]);

    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isDraggingRef.current) {
            isDraggingRef.current = false;
            return;
        }
        if (!progressBarRef.current || duration === 0) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const newTime = percent * duration;

        setCurrentTime(newTime);
        setHoverTime(null);
        seekToTime(newTime);
    };

    const handleProgressBarMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!progressBarRef.current || duration === 0) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const newTime = percent * duration;

        isDraggingRef.current = true;
        draggedTimeRef.current = newTime;
        setIsDragging(true);
        setCurrentTime(newTime);
        setHoverTime(null);

        let lastSeekTime = 0;

        const onMouseMove = (moveEvent: MouseEvent) => {
            if (!isDraggingRef.current || !progressBarRef.current) return;

            const moveRect = progressBarRef.current.getBoundingClientRect();
            const movePercent = Math.max(0, Math.min(1, (moveEvent.clientX - moveRect.left) / moveRect.width));
            const seekTime = movePercent * duration;

            draggedTimeRef.current = seekTime;
            setCurrentTime(seekTime);

            // Throttled seek (every 150ms)
            const now = Date.now();
            if (now - lastSeekTime >= 150) {
                lastSeekTime = now;
                seekToTime(seekTime);
            }
        };

        const onMouseUp = () => {
            const finalTime = draggedTimeRef.current;
            isDraggingRef.current = false;
            setIsDragging(false);
            setHoverTime(null);

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            seekToTime(finalTime);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const handleProgressBarTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!progressBarRef.current || duration === 0) return;

        const touch = e.touches[0];
        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
        const newTime = percent * duration;

        isDraggingRef.current = true;
        draggedTimeRef.current = newTime;
        setIsDragging(true);
        setCurrentTime(newTime);
        setHoverTime(null);

        let lastSeekTime = 0;

        const onTouchMove = (moveEvent: TouchEvent) => {
            if (!isDraggingRef.current || !progressBarRef.current) return;

            const moveTouch = moveEvent.touches[0];
            const moveRect = progressBarRef.current.getBoundingClientRect();
            const movePercent = Math.max(0, Math.min(1, (moveTouch.clientX - moveRect.left) / moveRect.width));
            const seekTime = movePercent * duration;

            draggedTimeRef.current = seekTime;
            setCurrentTime(seekTime);

            // Throttled seek (every 150ms)
            const now = Date.now();
            if (now - lastSeekTime >= 150) {
                lastSeekTime = now;
                seekToTime(seekTime);
            }
        };

        const cleanup = () => {
            const finalTime = draggedTimeRef.current;
            isDraggingRef.current = false;
            setIsDragging(false);
            setHoverTime(null);

            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', cleanup);
            document.removeEventListener('touchcancel', cleanup);

            seekToTime(finalTime);
        };

        document.addEventListener('touchmove', onTouchMove, { passive: true });
        document.addEventListener('touchend', cleanup);
        document.addEventListener('touchcancel', cleanup);
    };

    const handlePlayPause = async () => {
        if (!playerRef.current) return;
        const playerInstance = playerRef.current as any;
        try {
            if (centerIconTimeoutRef.current) {
                clearTimeout(centerIconTimeoutRef.current);
                centerIconTimeoutRef.current = null;
            }

            if (isPlaying) {
                await playerInstance.pause();
                setIsPlaying(false);
                setShowCenterIcon(true);
                setIconAnimation('zoom-out');

                centerIconTimeoutRef.current = setTimeout(() => {
                    setIconAnimation('none');
                    centerIconTimeoutRef.current = null;
                }, 400);
            } else {
                await playerInstance.play();
                setIsPlaying(true);
                setIconAnimation('zoom-in');
                setShowCenterIcon(true);

                centerIconTimeoutRef.current = setTimeout(() => {
                    setShowCenterIcon(false);
                    setIconAnimation('none');
                    centerIconTimeoutRef.current = null;
                }, 500);
            }
        } catch {
            // Ignore play/pause errors
        }
    };

    const handleVolumeChange = (newVolume: number) => {
        if (!playerRef.current) return;
        const playerInstance = playerRef.current as any;
        const volumeValue = Math.max(0, Math.min(1, newVolume));

        try {
            if (typeof playerInstance.setVolume === 'function') {
                playerInstance.setVolume(volumeValue);
            }

            if (volumeValue > 0) {
                if (typeof playerInstance.unmute === 'function') {
                    playerInstance.unmute();
                } else if (typeof playerInstance.setMuted === 'function') {
                    playerInstance.setMuted(false);
                }
            }

            if (playerInstance.getVideoElement) {
                const videoElement = playerInstance.getVideoElement();
                if (videoElement) {
                    videoElement.volume = volumeValue;
                    videoElement.muted = volumeValue === 0;
                }
            }
        } catch {
            // Ignore volume errors
        }

        setVolume(volumeValue);
        setIsMuted(volumeValue === 0);
    };

    const handleMuteToggle = () => {
        if (!playerRef.current) return;
        const playerInstance = playerRef.current as any;

        try {
            if (isMuted || volume === 0) {
                const newVolume = volume > 0 ? volume : 0.5;

                if (typeof playerInstance.unmute === 'function') {
                    playerInstance.unmute();
                }
                if (typeof playerInstance.setVolume === 'function') {
                    playerInstance.setVolume(newVolume);
                }
                if (playerInstance.getVideoElement) {
                    const videoElement = playerInstance.getVideoElement();
                    if (videoElement) {
                        videoElement.muted = false;
                        videoElement.volume = newVolume;
                    }
                }

                setVolume(newVolume);
                setIsMuted(false);
            } else {
                if (typeof playerInstance.mute === 'function') {
                    playerInstance.mute();
                }
                if (playerInstance.getVideoElement) {
                    const videoElement = playerInstance.getVideoElement();
                    if (videoElement) {
                        videoElement.muted = true;
                    }
                }

                setIsMuted(true);
            }
        } catch {
            // Ignore mute errors
        }
    };

    const handleFullscreen = () => {
        if (!playerContainerWrapperRef.current) return;

        if (!isFullscreen) {
            if (playerContainerWrapperRef.current.requestFullscreen) {
                playerContainerWrapperRef.current.requestFullscreen();
            } else if ((playerContainerWrapperRef.current as any).webkitRequestFullscreen) {
                (playerContainerWrapperRef.current as any).webkitRequestFullscreen();
            } else if ((playerContainerWrapperRef.current as any).mozRequestFullScreen) {
                (playerContainerWrapperRef.current as any).mozRequestFullScreen();
            } else if ((playerContainerWrapperRef.current as any).msRequestFullscreen) {
                (playerContainerWrapperRef.current as any).msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            } else if ((document as any).mozCancelFullScreen) {
                (document as any).mozCancelFullScreen();
            } else if ((document as any).msExitFullscreen) {
                (document as any).msExitFullscreen();
            }
        }
    };

    const handlePictureInPicture = async () => {
        try {
            const playerInstance = playerRef.current as any;

            let videoElement: HTMLVideoElement | null = null;

            if (playerInstance?.getVideoElement) {
                videoElement = playerInstance.getVideoElement();
            } else if (playerContainerRef.current) {
                videoElement = playerContainerRef.current.querySelector('video');
            }

            if (!videoElement) return;

            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
                setIsPiP(false);
            } else if (document.pictureInPictureEnabled) {
                await videoElement.requestPictureInPicture();
                setIsPiP(true);
            }
        } catch {
            // Ignore PiP errors
        }
    };

    // Handle fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Auto-hide controls on mouse move
    useEffect(() => {
        const handleMouseMove = () => {
            setShowControls(true);
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            controlsTimeoutRef.current = setTimeout(() => {
                if (isPlaying) {
                    setShowControls(false);
                }
            }, 3000);
        };

        const containerElement = playerContainerWrapperRef.current;
        if (containerElement) {
            containerElement.addEventListener('mousemove', handleMouseMove);
            containerElement.addEventListener('mouseenter', () => setShowControls(true));
        }

        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            if (containerElement) {
                containerElement.removeEventListener('mousemove', handleMouseMove);
            }
        };
    }, [isPlaying]);

    const handleQualityChange = (quality: string) => {
        if (!playerRef.current) return;

        try {
            const playerInstance = playerRef.current as any;

            // Handle 'auto' quality - enable adaptive streaming
            if (quality === 'auto') {
                // Enable adaptation for ABR (Adaptive Bitrate)
                if (playerInstance.adaptation) {
                    if (typeof playerInstance.adaptation.setEnabled === 'function') {
                        playerInstance.adaptation.setEnabled(true);
                    }
                }
                setCurrentQuality(quality);
                return;
            }

            // Parse resolution height from quality string (e.g., "1080p" -> 1080)
            const targetHeight = parseInt(quality.replace('p', ''), 10);

            // Get available qualities from player
            const playerQualities = playerInstance.getAvailableVideoQualities?.() || [];

            // Find the quality that matches the target height
            const targetQuality = playerQualities.find((q: any) => {
                const qHeight = q.height || q.resolution?.height || 0;
                return qHeight === targetHeight;
            });

            if (!targetQuality) {
                // If exact match not found, find closest available quality
                const closestQuality = playerQualities.reduce((prev: any, curr: any) => {
                    const prevHeight = prev?.height || prev?.resolution?.height || 0;
                    const currHeight = curr?.height || curr?.resolution?.height || 0;
                    return Math.abs(currHeight - targetHeight) < Math.abs(prevHeight - targetHeight) ? curr : prev;
                }, playerQualities[0]);

                if (closestQuality) {
                    // Disable adaptation before setting manual quality
                    if (playerInstance.adaptation?.setEnabled) {
                        playerInstance.adaptation.setEnabled(false);
                    }

                    if (typeof playerInstance.setVideoQuality === 'function') {
                        playerInstance.setVideoQuality(closestQuality);
                    }
                }
                setCurrentQuality(quality);
                return;
            }

            // Disable adaptation (ABR) before setting manual quality
            if (playerInstance.adaptation?.setEnabled) {
                playerInstance.adaptation.setEnabled(false);
            }

            // Set the video quality
            if (typeof playerInstance.setVideoQuality === 'function') {
                playerInstance.setVideoQuality(targetQuality);
            }

            setCurrentQuality(quality);
        } catch (error) {
            console.error('[BitMovinVideo] Quality change error:', error);
        }
    };

    const handleSpeedChange = (speed: string) => {
        if (!playerRef.current) return;

        try {
            const playerInstance = playerRef.current as any;
            const speedValue = speed === 'normal' ? 1 : parseFloat(speed.replace('x', ''));

            if (playerInstance.setPlaybackSpeed) {
                playerInstance.setPlaybackSpeed(speedValue);
            } else if (playerInstance.setSpeed) {
                playerInstance.setSpeed(speedValue);
            } else if (playerInstance.playbackSpeed !== undefined) {
                playerInstance.playbackSpeed = speedValue;
            } else if (playerInstance.getVideoElement) {
                const videoElement = playerInstance.getVideoElement();
                if (videoElement) {
                    videoElement.playbackRate = speedValue;
                }
            }

            setCurrentSpeed(speed);
        } catch {
            // Ignore speed change errors
        }
    };

    // Close settings menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // Don't close if clicking on the settings button (let the button's onClick handle toggle)
            if (settingsButtonRef.current && settingsButtonRef.current.contains(target)) {
                return;
            }
            if (settingsMenuRef.current && !settingsMenuRef.current.contains(target)) {
                setShowSettings(false);
            }
        };

        if (showSettings) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSettings]);

    const handleProgressBarMouseMoveHover = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isDraggingRef.current) return;
        if (!progressBarRef.current || duration === 0) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const time = percent * duration;
        setHoverTime(time);
        setHoverPosition(percent * 100);
    };

    const handleProgressBarMouseLeaveHover = () => {
        setHoverTime(null);
    };

    const zoomAnimationStyles = `
        @keyframes zoomIn {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes zoomOut {
            0% { transform: scale(0.5); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }
        @keyframes zoomOutAnim {
            0% { 
                transform: scale(1.5); 
                opacity: 0.5;
            }
            100% { 
                transform: scale(1); 
                opacity: 1;
            }
        }
    `;

    const volumeSliderStyles = `
        .volume-slider {
            -webkit-appearance: none;
            appearance: none;
            height: 4px;
            border-radius: 9999px;
        }
        
        .volume-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 12px;
            height: 12px;
            background: #10b981;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        
        .volume-slider::-moz-range-thumb {
            width: 12px;
            height: 12px;
            background: #10b981;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        
        .volume-slider::-ms-thumb {
            width: 12px;
            height: 12px;
            background: #10b981;
            border-radius: 50%;
            cursor: pointer;
            border: none;
        }
    `;

    const videoPlayerStyles = `
        .bmpui-ui-uicontainer {
            width: 100% !important;
            height: 100% !important;
        }
        
        .bmpui-ui-uicontainer video {
            width: 100% !important;
            height: 100% !important;
        }
        
        .bmpui-container-wrapper {
            width: 100% !important;
            height: 100% !important;
        }
    `;

    return (
        <>
            <style>{zoomAnimationStyles}</style>
            <style>{volumeSliderStyles}</style>
            <style>{videoPlayerStyles}</style>
            <div
                ref={playerContainerWrapperRef}
                className={`relative bg-black overflow-hidden group w-full ${className || 'aspect-video'}`}
                style={aspectRatio ? { aspectRatio } : undefined}
                onMouseEnter={() => setShowControls(true)}
                onMouseLeave={() => {
                    if (isPlaying) {
                        setTimeout(() => setShowControls(false), 2000);
                    }
                }}
            >
                {/* Clickable Video Overlay - Click anywhere to Play/Pause */}
                {playerReady && (
                    <div
                        className="absolute inset-0 z-30 cursor-pointer"
                        onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('.controls-bar') ||
                                target.closest('.settings-menu') ||
                                target.closest('.top-controls')) {
                                return;
                            }
                            handlePlayPause();
                        }}
                    />
                )}

                <div
                    ref={playerContainerRef}
                    className="absolute inset-0 w-full h-full [&>div]:w-full [&>div]:h-full [&_video]:w-full [&_video]:h-full"
                />

                {/* Center Play/Pause Button */}
                {playerReady && (showCenterIcon || !isPlaying) && (
                    <div className="absolute inset-0 flex items-center justify-center z-35 pointer-events-none">
                        <div
                            className={`rounded-full pointer-events-auto cursor-pointer p-3
                                ${iconAnimation === 'zoom-in'
                                    ? 'scale-150 opacity-0 transition-all duration-500 ease-out'
                                    : iconAnimation === 'zoom-out'
                                        ? 'scale-100 opacity-100 transition-all duration-400 ease-out'
                                        : 'scale-100 hover:bg-black/60 hover:scale-110 transition-all duration-200'}
                            `}
                            style={{
                                ...(iconAnimation === 'zoom-out' && {
                                    animation: 'zoomOutAnim 0.4s ease-out forwards'
                                })
                            }}
                            onClick={handlePlayPause}
                        >
                            {isPlaying ? (
                                // <PauseIcon className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-lg" />
                                <button type="button" className="w-16 h-16 sm:w-20 sm:h-20">
                                    <svg width="100%" height="100%" viewBox="0 0 48 48" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="24" cy="24" r="22" stroke="white" strokeWidth="1" />
                                        <rect x="19" y="16" width="1" height="16" fill="white" />
                                        <rect x="28" y="16" width="1" height="16" fill="white" />
                                    </svg>
                                </button>
                            ) : (
                                // <PlayIcon className="w-16 h-16 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                                <button type="button" className="w-16 h-16 sm:w-20 sm:h-20">
                                    <svg width="100%" height="100%" viewBox="0 0 48 48" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="24" cy="24" r="22" stroke="white" strokeWidth="1" />
                                        <polygon points="20,16 34,24 20,32" fill="white" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="text-white text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                            <p>Loading player...</p>
                        </div>
                    </div>
                )}

                {/* Error Overlay */}
                {hasError && (
                    <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
                        <div className="text-white text-center p-4">
                            <div className="text-4xl mb-4">⚠️</div>
                            <h3 className="text-xl font-semibold mb-2">Player Error</h3>
                            <p className="text-gray-300 mb-4">{errorMessage}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* Custom Controls Bar */}
                {playerReady && showControls && (
                    <>
                        {/* Top Right Controls - Mobile: visible, Desktop: hidden */}
                        <div className="top-controls sm:hidden absolute top-2 right-2 z-40 flex items-center gap-1 bg-black/40 rounded-lg p-1">
                            {/* PiP Button - Only shown in browsers that support the standard PiP API (Chrome, Edge, Safari) */}
                            {/* Firefox has its own native PiP button, so we hide ours there */}
                            {supportsPiP && (
                                <button
                                    onClick={handlePictureInPicture}
                                    className="text-white/80 p-1.5 rounded transition-all hover:text-white"
                                    title={isPiP ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'}
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Back rectangle (larger screen) - outline only */}
                                        <rect x="1" y="1" width="19" height="14" rx="1.5" stroke={isPiP ? "#3B82F6" : "white"} strokeWidth="2" fill="none" />
                                        {/* Front rectangle (smaller PiP window) - filled */}
                                        <rect x="8" y="9" width="15" height="10" rx="1.5" fill={isPiP ? "#3B82F6" : "white"} />
                                    </svg>
                                </button>
                            )}

                            {/* Volume Button */}
                            <button
                                onClick={handleMuteToggle}
                                className="text-white/80 p-1.5 rounded transition-all hover:text-white"
                                title={isMuted ? 'Unmute' : 'Mute'}
                            >
                                {isMuted || volume === 0 ? (
                                    <SpeakerXMarkIcon className="w-4 h-4" />
                                ) : (
                                    <SpeakerWaveIcon className="w-4 h-4" />
                                )}
                            </button>

                            {/* Settings Button */}
                            <button
                                ref={settingsButtonRef}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSettings(!showSettings);
                                }}
                                className="text-white/80 p-1.5 rounded transition-all hover:text-white"
                                title="Settings"
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
                                        stroke="white"
                                        strokeWidth="2"
                                        fill="none"
                                    />
                                    <path
                                        d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                    />
                                </svg>
                            </button>

                            {/* Fullscreen Button */}
                            <button
                                onClick={handleFullscreen}
                                className="text-white/80 p-1.5 rounded transition-all hover:text-white"
                                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                            >
                                {isFullscreen ? (
                                    /* Exit fullscreen - arrows pointing inward */
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Bottom-left arrow pointing inward */}
                                        <path d="M3 21L10 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M3 14H10V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                        {/* Top-right arrow pointing inward */}
                                        <path d="M21 3L14 10" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M21 10H14V3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                ) : (
                                    /* Enter fullscreen - arrows pointing outward */
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Bottom-left arrow pointing outward */}
                                        <path d="M10 14L3 21" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M3 14V21H10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                        {/* Top-right arrow pointing outward */}
                                        <path d="M14 10L21 3" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M21 10V3H14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* Bottom Controls Bar */}
                        <div className="controls-bar absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 sm:p-4 z-40 transition-opacity duration-300">
                            {/* Progress Bar with Time Display */}
                            <div className="mb-0 sm:mb-2">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    {/* Current Time - Left Side */}
                                    <span className="text-white text-[10px] sm:text-sm font-medium min-w-[28px] sm:min-w-[40px] tabular-nums">
                                        {formatTime(currentTime)}
                                    </span>

                                    {/* Progress Bar */}
                                    <div className="flex-1 py-1 sm:py-2">
                                        <div
                                            ref={progressBarRef}
                                            className="h-4 sm:h-5 bg-transparent cursor-pointer relative flex items-center touch-none"
                                            onMouseDown={handleProgressBarMouseDown}
                                            onTouchStart={handleProgressBarTouchStart}
                                            onClick={handleProgressBarClick}
                                            onMouseMove={handleProgressBarMouseMoveHover}
                                            onMouseLeave={handleProgressBarMouseLeaveHover}
                                        >
                                            {/* Time Preview Tooltip - ONLY show during drag, NOT on hover for mobile */}
                                            {isDragging && (
                                                <div
                                                    className="absolute bottom-full mb-2 -translate-x-1/2 h-10 w-16 flex items-end justify-center p-1 bg-gradient-to-t from-black/70 to-transparent text-white text-xs font-medium border border-white/50 whitespace-nowrap pointer-events-none z-20"
                                                    style={{
                                                        left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
                                                    }}
                                                >
                                                    {formatTime(currentTime)}
                                                </div>
                                            )}

                                            {/* Desktop hover tooltip - hidden on touch devices */}
                                            {hoverTime !== null && !isDragging && (
                                                <div
                                                    className="hidden sm:flex absolute bottom-full mb-2 -translate-x-1/2 h-10 w-16 items-end justify-center p-1 bg-gradient-to-t from-black/70 to-transparent text-white text-xs font-medium border border-white/50 whitespace-nowrap pointer-events-none z-20"
                                                    style={{
                                                        left: `${hoverPosition}%`
                                                    }}
                                                >
                                                    {formatTime(hoverTime)}
                                                </div>
                                            )}

                                            {/* Visual track - thin bar */}
                                            <div className="absolute left-0 right-0 h-1 bg-gray-600 rounded-full pointer-events-none">
                                                <div
                                                    className="h-full bg-green-500 rounded-full"
                                                    style={{
                                                        width: `${duration > 0 && !isNaN(duration) && !isNaN(currentTime)
                                                            ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
                                                            : 0}%`
                                                    }}
                                                />
                                                {/* Hover preview bar */}
                                                {hoverTime !== null && !isDragging && (
                                                    <div
                                                        className="absolute top-0 h-full bg-white/30 rounded-full pointer-events-none"
                                                        style={{ width: `${hoverPosition}%` }}
                                                    />
                                                )}
                                            </div>

                                            {/* Draggable thumb/pointer */}
                                            <div
                                                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full -translate-x-1/2 cursor-pointer z-10 touch-none"
                                                style={{
                                                    left: `${duration > 0 && !isNaN(duration) && !isNaN(currentTime)
                                                        ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
                                                        : 0}%`
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    handleProgressBarMouseDown(e);
                                                }}
                                                onTouchStart={(e) => {
                                                    e.stopPropagation();
                                                    handleProgressBarTouchStart(e);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Duration - Right Side */}
                                    <span className="text-white text-[10px] sm:text-sm font-medium min-w-[28px] sm:min-w-[40px] text-right tabular-nums">
                                        {formatTime(duration)}
                                    </span>
                                </div>
                            </div>

                            {/* Desktop Controls Row - Hidden on mobile */}
                            <div className="hidden sm:flex items-center justify-between">
                                {/* Left Side Controls */}
                                <div className="flex items-center gap-2 sm:gap-4">
                                    {/* Play/Pause Button */}
                                    <button
                                        onClick={handlePlayPause}
                                        className="text-white hover:text-gray-300 transition-colors"
                                        title={isPlaying ? 'Pause' : 'Play'}
                                    >
                                        {isPlaying ? (
                                            <svg className="w-5 h-5 sm:w-8 sm:h-8" viewBox="0 0 48 48" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="24" cy="24" r="22" stroke="white" strokeWidth="2" />
                                                <rect x="19" y="16" width="2" height="16" fill="white" />
                                                <rect x="28" y="16" width="2" height="16" fill="white" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5 sm:w-8 sm:h-8" viewBox="0 0 48 48" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="24" cy="24" r="22" stroke="white" strokeWidth="2" />
                                                <polygon points="20,16 34,24 20,32" fill="white" />
                                            </svg>
                                        )}
                                    </button>

                                    {/* Volume Control */}
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        <button
                                            onClick={handleMuteToggle}
                                            className="group text-white transition-all duration-300"
                                            title={isMuted ? 'Unmute' : 'Mute'}
                                        >
                                            <svg className="w-4 h-4 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                {/* Speaker body */}
                                                <path d="M3 9v6h4l5 5V4L7 9H3z" fill="white" />
                                                {isMuted || volume === 0 ? (
                                                    /* X mark when muted */
                                                    <path d="M16 9l6 6m0-6l-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                ) : (
                                                    /* Volume waves based on level */
                                                    <>
                                                        {/* First wave - always visible when not muted */}
                                                        <path d="M14 9.5c1 0.5 1.5 1.5 1.5 2.5s-0.5 2-1.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={volume > 0 ? 1 : 0} />
                                                        {/* Second wave - visible at medium volume */}
                                                        <path d="M16 7c2 1 3 3 3 5s-1 4-3 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={volume > 0.33 ? 1 : 0} />
                                                        {/* Third wave - visible at high volume */}
                                                        <path d="M18 4.5c3 1.5 4.5 4.5 4.5 7.5s-1.5 6-4.5 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={volume > 0.66 ? 1 : 0} />
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                        {/* Volume Slider */}
                                        <div className="flex items-center w-16 sm:w-20">
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.01"
                                                value={isMuted ? 0 : volume}
                                                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                                                className="w-full h-1 bg-gray-600 rounded-lg cursor-pointer volume-slider"
                                                style={{
                                                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${(isMuted ? 0 : volume) * 100}%, #4b5563 ${(isMuted ? 0 : volume) * 100}%, #4b5563 100%)`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side Controls */}
                                <div className="flex items-center gap-1 sm:gap-2">
                                    {/* PiP Button - Only shown in browsers that support the standard PiP API (Chrome, Edge, Safari) */}
                                    {/* Firefox has its own native PiP button, so we hide ours there */}
                                    {supportsPiP && (
                                        <button
                                            onClick={handlePictureInPicture}
                                            className="text-white/70 p-1.5 sm:p-2 rounded transition-all hover:text-white"
                                            title={isPiP ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'}
                                        >
                                            <svg className="w-4 h-4 sm:w-6 sm:h-6 pt-0.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                {/* Back rectangle (larger screen) - outline only */}
                                                <rect x="1" y="1" width="19" height="14" rx="1.5" stroke={isPiP ? "#3B82F6" : "white"} strokeWidth="2" fill="none" />
                                                {/* Front rectangle (smaller PiP window) - filled */}
                                                <rect x="8" y="9" width="15" height="10" rx="1.5" fill={isPiP ? "#3B82F6" : "white"} />
                                            </svg>
                                        </button>
                                    )}

                                    {/* Settings Button */}
                                    <button
                                        ref={settingsButtonRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowSettings(!showSettings);
                                        }}
                                        className="text-white/70 p-1.5 sm:p-2 rounded transition-all hover:text-white"
                                        title="Settings"
                                    >
                                        <svg className="w-4 h-4 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
                                                stroke="white"
                                                strokeWidth="2"
                                                fill="none"
                                            />
                                            <path
                                                d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                fill="none"
                                            />
                                        </svg>
                                    </button>

                                    {/* Fullscreen Button */}
                                    <button
                                        onClick={handleFullscreen}
                                        className="text-white/70 p-1.5 sm:p-2 rounded transition-all hover:text-white"
                                        title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                    >
                                        {isFullscreen ? (
                                            /* Exit fullscreen - arrows pointing inward */
                                            <svg className="w-4 h-4 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                {/* Bottom-left arrow pointing inward */}
                                                <path d="M3 21L10 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M3 14H10V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                {/* Top-right arrow pointing inward */}
                                                <path d="M21 3L14 10" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M21 10H14V3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                            </svg>
                                        ) : (
                                            /* Enter fullscreen - arrows pointing outward */
                                            <svg className="w-4 h-4 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                {/* Bottom-left arrow pointing outward */}
                                                <path d="M10 14L3 21" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M3 14V21H10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                {/* Top-right arrow pointing outward */}
                                                <path d="M14 10L21 3" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M21 10V3H14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Settings Menu Overlay */}
                {playerReady && showSettings && (
                    <div
                        ref={settingsMenuRef}
                        className="settings-menu absolute inset-0 sm:inset-auto sm:bottom-28 sm:right-4 bg-black/70 sm:bg-black/60 backdrop-blur-md sm:backdrop-blur-sm sm:rounded-lg p-4 sm:p-4 z-50 sm:shadow-xl flex flex-col justify-center items-center md:block"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button - only visible on mobile */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowSettings(false);
                            }}
                            className="absolute top-3 right-3 text-white/80 hover:text-white p-1 md:hidden"
                            title="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Arrow pointer - only visible on desktop */}
                        <div className="hidden sm:block absolute -bottom-1.5 right-6 w-3 h-3 bg-black/60 rotate-45" />

                        <div className="space-y-4 w-full max-w-[200px] md:max-w-none md:w-auto">
                            {/* Video Quality Row */}
                            <div className="flex items-center justify-between gap-2 md:gap-4">
                                <label className="text-white text-sm sm:text-sm font-medium whitespace-nowrap">
                                    Quality
                                </label>
                                {/* Video Quality Select */}
                                <select
                                    value={currentQuality}
                                    onChange={(e) => handleQualityChange(e.target.value)}
                                    className="bg-black/90 text-white text-sm sm:text-sm border border-white/40 rounded px-3 md:px-3 py-2 md:py-2 w-[130px] sm:w-[120px] focus:outline-none focus:border-white cursor-pointer appearance-none text-left pr-8 md:pr-8"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 8px center',
                                        backgroundSize: '12px'
                                    }}
                                >
                                    <option value="auto" className="bg-gray-900 text-left">auto</option>
                                    <option value="1080p" className="bg-gray-900 text-left">1920x1080, 2948kbps</option>
                                    <option value="720p" className="bg-gray-900 text-left">1280x720, 2106kbps</option>
                                    <option value="480p" className="bg-gray-900 text-left">1024x576, 1517kbps</option>
                                    <option value="360p" className="bg-gray-900 text-left">768x432, 921kbps</option>
                                    <option value="240p" className="bg-gray-900 text-left">512x288, 719kbps</option>
                                </select>
                            </div>

                            {/* Speed Row */}
                            <div className="flex items-center justify-between gap-4 md:gap-4">
                                <label className="text-white text-sm md:text-sm font-medium whitespace-nowrap">
                                    Speed
                                </label>
                                {/* Speed Select */}
                                <select
                                    value={currentSpeed}
                                    onChange={(e) => handleSpeedChange(e.target.value)}
                                    className="bg-black/90 text-white text-sm md:text-sm border border-white/40 rounded px-3 md:px-3 py-2 md:py-2 w-[130px] md:w-[120px] focus:outline-none focus:border-white cursor-pointer appearance-none text-left pr-8 md:pr-8"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 8px center',
                                        backgroundSize: '12px'
                                    }}
                                >
                                    <option value="0.5x" className="bg-gray-900 text-left">0.5x</option>
                                    <option value="0.75x" className="bg-gray-900 text-left">0.75x</option>
                                    <option value="normal" className="bg-gray-900 text-left">normal</option>
                                    <option value="1.25x" className="bg-gray-900 text-left">1.25x</option>
                                    <option value="1.5x" className="bg-gray-900 text-left">1.5x</option>
                                    <option value="1.75x" className="bg-gray-900 text-left">1.75x</option>
                                    <option value="2x" className="bg-gray-900 text-left">2x</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
